import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { ApiError } from '../types';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const API_TIMEOUT = 30000; // 30 seconds
export const SESSION_EXPIRED_EVENT = 'aiona:session-expired';

const dispatchSessionExpiredEvent = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const event = new CustomEvent(SESSION_EXPIRED_EVENT, {
    cancelable: true,
  });
  return !window.dispatchEvent(event);
};

// Create axios instance
// Cookie認証は廃止し、Authorization header のみを使用
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,  // Cookie送信は無効化（Authorization header認証のみ）
});

// Token management - using sessionStorage
// Tokens are stored in sessionStorage and sent via Authorization header

// Request interceptor
// Adds Authorization header from sessionStorage
apiClient.interceptors.request.use(
  (config) => {
    // Get access token from sessionStorage and add to Authorization header
    const token = sessionStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // CSRF token は不要（Cookie認証廃止により）

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Handle 403 Forbidden - don't retry, permission denied
    if (error.response?.status === 403) {
      console.error('403 Forbidden:', error.response?.data);
      const apiError: ApiError = {
        message: error.response?.data?.message || 'Access forbidden',
        code: error.response?.data?.code,
        details: error.response?.data?.details,
      };
      return Promise.reject(apiError);
    }

    // Handle 429 Too Many Requests separately — must not be confused with
    // an auth failure. If a burst of requests trips the per-user rate
    // limit, the next /auth/refresh attempt would itself return 429; the
    // generic 401 branch below would then treat that as a failed refresh
    // and force-logout the user (observed 2026-05-14 demo flow when
    // opening DocumentDetailPage's metadata tab fired N parallel image
    // blob fetches). Surface the retry hint to the caller without
    // touching session tokens.
    if (error.response?.status === 429) {
      // The backend rate-limiter returns { detail, retry_after } rather
      // than the canonical ApiError shape, so widen the type for this branch.
      const rateLimitBody = error.response.data as
        | { detail?: string; message?: string; retry_after?: number }
        | undefined;
      const retryAfterHeader = error.response.headers?.['retry-after'];
      const retryAfter = retryAfterHeader ? Number(retryAfterHeader) : undefined;
      const apiError: ApiError = {
        message: rateLimitBody?.detail || rateLimitBody?.message || 'Rate limit exceeded',
        code: 'RATE_LIMIT_EXCEEDED',
        details: { retry_after: retryAfter ?? rateLimitBody?.retry_after },
      };
      return Promise.reject(apiError);
    }

    // Handle 401 errors - attempt token refresh (only once)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Don't try to refresh if already on login page or auth endpoints
      const publicPaths = ['/login', '/forgot-password', '/reset-password'];
      const currentPath = window.location.pathname;
      const isPublicRoute = publicPaths.some(path => currentPath.startsWith(path));
      const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                            originalRequest.url?.includes('/auth/refresh');

      if (isPublicRoute || isAuthEndpoint) {
        // Don't redirect if already on public route or auth endpoint
        const apiError: ApiError = {
          message: error.response?.data?.message || 'Authentication required',
          code: error.response?.data?.code,
          details: error.response?.data?.details,
        };
        return Promise.reject(apiError);
      }

      try {
        // Attempt to refresh token using refresh_token from sessionStorage
        const refreshToken = sessionStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await apiClient.post('/auth/refresh', {
          refresh_token: refreshToken
        });

        // Store new tokens
        if (response.data.access_token) {
          sessionStorage.setItem('access_token', response.data.access_token);
        }
        if (response.data.refresh_token) {
          sessionStorage.setItem('refresh_token', response.data.refresh_token);
        }

        // Retry original request with new token
        return apiClient(originalRequest);
      } catch (refreshError) {
        // If /auth/refresh itself was rate-limited, the session is still
        // valid — it just couldn't be refreshed right now. Don't clear
        // tokens or redirect to /login in that case (Codex P1 review on
        // PR #668): the upstream 429 branch above rejects with
        // ``code: 'RATE_LIMIT_EXCEEDED'``, which the original 401 flow
        // would otherwise treat as an auth failure and force-logout the
        // user — re-introducing the exact incident this PR is fixing.
        const refreshApiError = refreshError as ApiError | undefined;
        if (refreshApiError?.code === 'RATE_LIMIT_EXCEEDED') {
          return Promise.reject(refreshError);
        }
        // Refresh failed for an auth reason, clear tokens and redirect to login
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('token_expiry');
        const handledByApp = dispatchSessionExpiredEvent();
        if (!isPublicRoute && !handledByApp) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // Format error response
    const apiError: ApiError = {
      message: error.response?.data?.message || error.message || 'An error occurred',
      code: error.response?.data?.code,
      details: error.response?.data?.details,
    };

    return Promise.reject(apiError);
  }
);

// API methods
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then(res => res.data),

  post: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then(res => res.data),

  put: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then(res => res.data),

  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then(res => res.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then(res => res.data),
};

// File upload with progress
// Note: Do NOT set Content-Type manually for FormData - axios will set it
// automatically with the correct boundary parameter
export const uploadFile = async (
  url: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);

  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  };

  return apiClient.post(url, formData, config).then(res => res.data);
};

export default apiClient;
